import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import MainLayout from '../components/MainLayout'
import NotesGrid from '../components/NotesGrid'
import NotesList from '../components/NotesList'
import TagFilter from '../components/TagFilter'
import BulkActions from '../components/BulkActions'
import SortDropdown from '../components/SortDropdown'
import Pagination from '../components/Pagination'
import PdfUploadDialog from '../components/PdfUploadDialog'
import { Note, Tag, getNotes } from '../api/notes'
import apiClient from '../api/client'

interface NotesPageProps {
  status?: 'notes' | 'starred' | 'archive' | 'removed'
}

const ITEMS_PER_PAGE_KEY = 'fastnotes_items_per_page'
const SORT_PREFERENCE_KEY = 'fastnotes_sort_preference'
const VIEW_MODE_KEY = 'fastnotes_view_mode'

const statusConfig: Record<string, { title: string; emptyMessage: string; emptyIcon: 'notes' | 'star' | 'archive' | 'trash'; viewStatus: string }> = {
  notes: { title: 'All Notes', emptyMessage: 'No notes yet', emptyIcon: 'notes', viewStatus: 'all' },
  starred: { title: 'Starred', emptyMessage: 'No starred notes', emptyIcon: 'star', viewStatus: 'starred' },
  archive: { title: 'Archive', emptyMessage: 'No archived notes', emptyIcon: 'archive', viewStatus: 'archive' },
  removed: { title: 'Trash', emptyMessage: 'Trash is empty', emptyIcon: 'trash', viewStatus: 'removed' },
}

// Get saved items per page from localStorage
const getSavedItemsPerPage = (): number => {
  try {
    const saved = localStorage.getItem(ITEMS_PER_PAGE_KEY)
    if (saved) {
      const parsed = parseInt(saved, 10)
      if ([10, 25, 50, 100].includes(parsed)) return parsed
    }
  } catch {
    // Ignore
  }
  return 25 // Default
}

// Get saved sort preference from localStorage
const getSavedSortPreference = (): { column: string; order: 'asc' | 'desc' } => {
  try {
    const saved = localStorage.getItem(SORT_PREFERENCE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      if (parsed.column && parsed.order) {
        return { column: parsed.column, order: parsed.order }
      }
    }
  } catch {
    // Ignore
  }
  return { column: 'created_time', order: 'desc' } // Default
}

// Get saved view mode from localStorage
const getSavedViewMode = (): 'grid' | 'list' => {
  try {
    const saved = localStorage.getItem(VIEW_MODE_KEY)
    if (saved === 'grid' || saved === 'list') {
      return saved
    }
  } catch {
    // Ignore
  }
  return 'grid' // Default
}

export default function NotesPage({ status = 'notes' }: NotesPageProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [notes, setNotes] = useState<Note[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(getSavedViewMode)
  const [totalCount, setTotalCount] = useState(0)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([])
  const [sortColumn, setSortColumn] = useState(() => getSavedSortPreference().column)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => getSavedSortPreference().order)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(getSavedItemsPerPage)

  const config = statusConfig[status]
  const searchQuery = searchParams.get('search') || ''
  const totalPages = Math.ceil(totalCount / itemsPerPage)

  // Parse selected tags from URL query params
  const getSelectedTagsFromUrl = (): Tag[] => {
    const tagsParam = searchParams.get('tags')
    if (!tagsParam) return []
    try {
      return JSON.parse(decodeURIComponent(tagsParam))
    } catch {
      return []
    }
  }

  const [selectedTags, setSelectedTags] = useState<Tag[]>(getSelectedTagsFromUrl)

  // Update selected tags and sync to URL
  const handleTagsChange = (tags: Tag[]) => {
    setSelectedTags(tags)
    const newParams = new URLSearchParams(searchParams)
    if (tags.length > 0) {
      newParams.set('tags', encodeURIComponent(JSON.stringify(tags.map(t => ({ id: t.id, name: t.name })))))
    } else {
      newParams.delete('tags')
    }
    setSearchParams(newParams, { replace: true })
  }

  // Fetch notes function
  const fetchNotes = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const skip = (currentPage - 1) * itemsPerPage
      const response = await getNotes(
        {
          view_status: config.viewStatus,
          search_keyword: searchQuery || undefined,
          selected_tags: selectedTags.length > 0 ? selectedTags : undefined,
        },
        sortColumn,
        sortOrder,
        skip,
        itemsPerPage
      )
      setNotes(response.data)
      setTotalCount(response.count)
    } catch (err) {
      console.error('Failed to fetch notes:', err)
      setError('Failed to load notes. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [config.viewStatus, searchQuery, selectedTags, sortColumn, sortOrder, currentPage, itemsPerPage])

  // Fetch notes on mount or when dependencies change
  useEffect(() => {
    fetchNotes()
  }, [fetchNotes])

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [status, searchQuery, selectedTags, sortColumn, sortOrder])

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    setSelectedNoteIds([]) // Clear selection when changing pages
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1) // Reset to first page
    setSelectedNoteIds([]) // Clear selection
    // Save to localStorage
    try {
      localStorage.setItem(ITEMS_PER_PAGE_KEY, String(newItemsPerPage))
    } catch {
      // Ignore
    }
  }

  // Handle sort change
  const handleSortChange = (column: string, order: 'asc' | 'desc') => {
    setSortColumn(column)
    setSortOrder(order)
  }

  // Handle view mode change
  const handleViewModeChange = (mode: 'grid' | 'list') => {
    setViewMode(mode)
    // Save to localStorage
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode)
    } catch {
      // Ignore
    }
  }

  // Toggle star status
  const handleToggleStar = async (noteId: string) => {
    const note = notes.find(n => n.id === noteId)
    if (!note) return

    try {
      await apiClient.put('/notes/partial_update_note', {
        note_ids: [noteId],
        note_partial: { is_starred: !note.is_starred }
      })
      // Update local state or refetch if on starred page
      if (status === 'starred' && note.is_starred) {
        setNotes(notes.filter(n => n.id !== noteId))
      } else {
        setNotes(notes.map(n =>
          n.id === noteId ? { ...n, is_starred: !n.is_starred } : n
        ))
      }
    } catch (err) {
      console.error('Failed to toggle star:', err)
    }
  }

  // Toggle home status
  const handleToggleHome = async (noteId: string) => {
    const note = notes.find(n => n.id === noteId)
    if (!note) return

    try {
      await apiClient.put('/notes/partial_update_note', {
        note_ids: [noteId],
        note_partial: { is_home: !note.is_home }
      })
      setNotes(notes.map(n =>
        n.id === noteId ? { ...n, is_home: !n.is_home } : n
      ))
    } catch (err) {
      console.error('Failed to toggle home:', err)
    }
  }

  // Delete note (move to trash)
  const handleDelete = async (noteId: string) => {
    const note = notes.find(n => n.id === noteId)
    if (!note) return

    try {
      await apiClient.put('/notes/partial_update_note', {
        note_ids: [noteId],
        note_partial: { is_thrashed: true }
      })
      setNotes(notes.filter(n => n.id !== noteId))
    } catch (err) {
      console.error('Failed to delete note:', err)
    }
  }

  // Selection handlers
  const handleSelectNote = (noteId: string) => {
    setSelectedNoteIds(prev =>
      prev.includes(noteId)
        ? prev.filter(id => id !== noteId)
        : [...prev, noteId]
    )
  }

  const handleSelectAll = () => {
    if (selectedNoteIds.length === notes.length) {
      setSelectedNoteIds([])
    } else {
      setSelectedNoteIds(notes.map(n => n.id))
    }
  }

  const handleClearSelection = () => {
    setSelectedNoteIds([])
  }

  const selectionMode = selectedNoteIds.length > 0

  const [showPdfDialog, setShowPdfDialog] = useState(false)

  return (
    <MainLayout>
      {/* Page Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* Select all checkbox (only in grid view) */}
          {viewMode === 'grid' && notes.length > 0 && (
            <input
              type="checkbox"
              checked={selectedNoteIds.length === notes.length && notes.length > 0}
              ref={(el) => {
                if (el) el.indeterminate = selectedNoteIds.length > 0 && selectedNoteIds.length < notes.length
              }}
              onChange={handleSelectAll}
              className="w-5 h-5 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2 cursor-pointer"
              title={selectedNoteIds.length === notes.length ? 'Deselect all' : 'Select all'}
            />
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{config.title}</h1>
            <p className="text-gray-600 mt-1">
              {selectionMode
                ? `${selectedNoteIds.length} of ${totalCount} selected`
                : status === 'removed'
                  ? 'Items in trash will be permanently deleted after 30 days'
                  : `${totalCount} note${totalCount !== 1 ? 's' : ''}`
              }
            </p>
          </div>
        </div>

        {/* View toggle and filter */}
        <div className="flex items-center space-x-2">
          {/* Sort dropdown */}
          <SortDropdown onSortChange={handleSortChange} />

          {/* Filter toggle button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            data-testid="notes-filter-btn"
            className={`p-2 rounded-lg transition-colors flex items-center gap-1 ${
              showFilters || selectedTags.length > 0
                ? 'bg-primary text-white' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
            title="Toggle filters"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {selectedTags.length > 0 && (
              <span className="text-xs font-medium">{selectedTags.length}</span>
            )}
          </button>

          <div className="w-px h-6 bg-gray-300"></div>

          {/* Upload PDF — only shown on the main notes list */}
          {status === 'notes' && (
            <button
              onClick={() => setShowPdfDialog(true)}
              data-testid="pdf-upload-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Upload PDF"
            >
              <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 7V3.5L18.5 9H13z"/>
              </svg>
              <span className="hidden sm:inline">PDF</span>
            </button>
          )}

          <button
            onClick={() => handleViewModeChange('grid')}
            data-testid="notes-view-grid-btn"
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'grid' 
                ? 'bg-primary text-white' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
            title="Grid view"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => handleViewModeChange('list')}
            data-testid="notes-view-list-btn"
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'list' 
                ? 'bg-primary text-white' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
            title="List view"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tag Filter Panel */}
      {showFilters && (
        <div className="mb-6">
          <TagFilter
            selectedTags={selectedTags}
            onTagsChange={handleTagsChange}
          />
        </div>
      )}

      {/* Active filters indicator */}
      {selectedTags.length > 0 && !showFilters && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600">Filtered by:</span>
          {selectedTags.map(tag => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
            >
              {tag.name}
              <button
                onClick={() => handleTagsChange(selectedTags.filter(t => t.id !== tag.id))}
                className="hover:text-primary-900"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
          <button
            onClick={() => handleTagsChange([])}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Search indicator */}
      {searchQuery && (
        <div className="mb-4 flex items-center text-sm text-gray-600">
          <span>Showing results for: <strong>"{searchQuery}"</strong></span>
          <button
            onClick={() => {
              const newParams = new URLSearchParams(searchParams)
              newParams.delete('search')
              setSearchParams(newParams, { replace: true })
            }}
            className="ml-2 text-primary hover:text-secondary"
          >
            Clear search
          </button>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-red-700">{error}</span>
            <button
              onClick={fetchNotes}
              className="ml-auto text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Notes display */}
      {viewMode === 'grid' ? (
        <NotesGrid
          notes={notes}
          isLoading={isLoading}
          emptyMessage={config.emptyMessage}
          emptyIcon={config.emptyIcon}
          onToggleStar={handleToggleStar}
          onToggleHome={handleToggleHome}
          selectedIds={selectedNoteIds}
          onSelect={handleSelectNote}
          selectionMode={selectionMode}
        />
      ) : (
        <NotesList
          notes={notes}
          isLoading={isLoading}
          emptyMessage={config.emptyMessage}
          onToggleStar={handleToggleStar}
          onToggleHome={handleToggleHome}
          onDelete={status !== 'removed' ? handleDelete : undefined}
          selectedIds={selectedNoteIds}
          onSelect={handleSelectNote}
          onSelectAll={handleSelectAll}
          selectionMode={selectionMode}
        />
      )}

      {/* Pagination */}
      {!isLoading && notes.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          className="border-t border-gray-200 mt-6"
        />
      )}

      {/* Bulk Actions */}
      <BulkActions
        selectedIds={selectedNoteIds}
        onClearSelection={handleClearSelection}
        onActionComplete={fetchNotes}
        currentStatus={status}
      />

      {/* PDF Upload Dialog */}
      {showPdfDialog && (
        <PdfUploadDialog
          onClose={() => setShowPdfDialog(false)}
          onSuccess={fetchNotes}
        />
      )}
    </MainLayout>
  )
}
